import { styled } from '@linaria/react';
import { Flex, Group, Select, SimpleGrid, Stack, Switch } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useTaskFit } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type { ModelProvider } from '../../../api/agent-miner-client/agent-miner-client.type';
import { FilterPanel } from '../../../components/filters/filter-panel';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { SingleValueCard } from '../../../components/overview-card/single-value-card';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { StyledButton } from '../../financials/charts/shared/chart-display-type-button-set';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';
import { MODEL_PROVIDER_LABELS, normalizeProvider } from '../model-comparison/model-comparison.type';
import { toDateOrNull, toIsoDateOrNull } from '../sessions-list/filters/session-filters.helpers';
import {
  DATE_OPTIONS,
  type DateRangePreset,
  RESET_KEYS,
  type SessionsFiltersValue,
} from '../sessions-list/filters/session-filters.type';
import { TaskFitMatrix } from './task-fit-matrix';
import { filterTaskFitColumns } from './task-fit.helpers';
import {
  useTaskFitDimension,
  useTaskFitEffectiveMetric,
  useTaskFitHiddenFamilies,
  useTaskFitParams,
  useTaskFitProviderFilter,
  useTaskFitSelectedCell,
  useTaskFitShowUnknown,
} from './task-fit.hooks';
import { TASK_FIT_METRICS } from './task-fit.type';
import type { TaskFitMetric } from './task-fit.type';

const DEFAULT_DATE_PRESET: DateRangePreset = '30d';

// Task Fit is window-scoped, so "All time" isn't offered on this tab.
const TASK_FIT_DATE_OPTIONS = DATE_OPTIONS.filter((option) => option.value !== 'all');

/**
 * Overview-slot content for the Task Fit tab: four KPI tiles summarizing the
 * window (agentic sessions, completed tasks, median cycle time, rework rate),
 * fed by the same query the matrix uses.
 *
 * @returns {JSX.Element} The KPI strip
 */
export const TaskFitOverview = () => {
  const { t } = useTranslation();
  const params = useTaskFitParams();
  const { data, query } = useTaskFit(params);
  const summary = data?.summary;

  return (
    <SimpleGrid cols={{ base: 2, lg: 4 }} spacing={16} data-testid="task-fit-kpi-strip">
      <SingleValueCard
        title={t('agent_miner.task_fit.agentic_sessions')}
        icon="terminal"
        value={summary ? summary.agentic_sessions.toLocaleString() : '—'}
        isLoading={query.isPending}
      />
      <SingleValueCard
        title={t('agent_miner.task_fit.completed_tasks')}
        icon="task_alt"
        value={summary ? summary.completed_tasks.toLocaleString() : '—'}
        isLoading={query.isPending}
      />
      <SingleValueCard
        title={t('agent_miner.task_fit.median_cycle_time')}
        icon="timer"
        value={fmtMetric(summary?.median_task_cycle_time_days)}
        unit={t('agent_miner.task_fit.median_cycle_time_unit')}
        isLoading={query.isPending}
      />
      <SingleValueCard
        title={t('agent_miner.task_fit.rework_rate')}
        icon="undo"
        value={fmtMetric(summary?.rework_rate_pct)}
        unit="%"
        isLoading={query.isPending}
      />
    </SimpleGrid>
  );
};

type TaskFitFiltersProps = {
  value: SessionsFiltersValue;
  setDateRange: (value: DateRangePreset) => void;
  setCustomRange: (start: string | null, end: string | null) => void;
  onClose: () => void;
};

/**
 * Side filter panel for the Task Fit tab: the heatmap metric select (rework,
 * cycle time, API cost) and the same date-window control the other Agent Miner
 * tabs use (shared URL params, so the range carries across tabs). The
 * row-dimension toggle stays a button pair in the main-section header, not here.
 *
 * @param {TaskFitFiltersProps} props - Component props
 * @returns {JSX.Element} The filter panel content
 */
export const TaskFitFilters = ({ value, setDateRange, setCustomRange, onClose }: TaskFitFiltersProps) => {
  const { t } = useTranslation();
  const params = useTaskFitParams();
  const { data } = useTaskFit(params);
  const { providers, setProviders } = useTaskFitProviderFilter();
  const { hiddenFamilies, setHiddenFamilies } = useTaskFitHiddenFamilies();
  const { showUnknown, setShowUnknown } = useTaskFitShowUnknown();
  // The same cost-gated metric TaskFitMain feeds to the matrix — a stale "cost"
  // selection degrades to rework for viewers without cost visibility, so the
  // select never offers/shows a dead option.
  const { effectiveMetric, costAllowed, setMetric } = useTaskFitEffectiveMetric();
  const [searchParams, setSearchParams] = useSearchParams();

  const metricLabels: Record<TaskFitMetric, string> = {
    rework: t('agent_miner.task_fit.metric_rework'),
    cycle_time: t('agent_miner.task_fit.metric_cycle_time'),
    cost: t('agent_miner.task_fit.metric_cost'),
  };
  const metricOptions = TASK_FIT_METRICS.filter((option) => option !== 'cost' || costAllowed).map((option) => ({
    value: option,
    label: metricLabels[option],
  }));

  // Distinct providers actually present in the current window's columns,
  // mirroring Model Comparison: an org that's never used a provider shouldn't
  // see an empty checkbox for it. Sorted alphabetically by label.
  const providerOptions = useMemo(() => {
    const fromColumns = new Set((data?.columns ?? []).map((column) => normalizeProvider(column.provider)));
    return Array.from(fromColumns)
      .map((provider) => ({ value: provider, label: MODEL_PROVIDER_LABELS[provider] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.columns]);

  // The model families the per-model toggle can act on: everything the provider
  // and hide-Unknown filters would show (hidden ones INCLUDED so they can be
  // re-shown), alphabetized to match the matrix's row order.
  const modelOptions = useMemo(() => {
    const candidates = filterTaskFitColumns(data?.columns ?? [], {
      providers,
      hiddenFamilies: new Set<string>(),
      showUnknown,
    });
    return [...candidates]
      .sort((a, b) => a.model_family.localeCompare(b.model_family))
      .map((column) => ({ value: column.model_family, label: column.model_family }));
  }, [data?.columns, providers, showUnknown]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    const groups: FilterGroup[] = [];
    if (providerOptions.length > 0) {
      groups.push({
        name: t('agent_miner.task_fit.provider'),
        icon: 'smart_toy',
        options: providerOptions,
        selectedValues: providers,
        onSelectionChange: (values) => setProviders(values as ModelProvider[]),
        optional: true,
        multiSelect: true,
      });
    }
    if (modelOptions.length > 0) {
      groups.push({
        name: t('agent_miner.task_fit.families_label'),
        icon: 'show_chart',
        options: modelOptions,
        selectedValues: modelOptions.map((option) => option.value).filter((family) => !hiddenFamilies.has(family)),
        // Toggling a model on/off writes the hidden set. Only the currently-listed
        // options are reconciled, so a family hidden then filtered out by a
        // provider change is preserved rather than silently un-hidden.
        onSelectionChange: (visibleValues) => {
          const next = new Set(hiddenFamilies);
          for (const option of modelOptions) {
            if (visibleValues.includes(option.value)) next.delete(option.value);
            else next.add(option.value);
          }
          setHiddenFamilies(next);
        },
        // Optional so the panel never auto-selects all when every model is
        // hidden — that would silently undo a "hide all" from the URL.
        optional: true,
      });
    }
    return groups;
  }, [providerOptions, providers, setProviders, modelOptions, hiddenFamilies, setHiddenFamilies, t]);

  // Build one combined URLSearchParams and write it in a single call so the
  // shared date setters and the tab-local params don't race off the same stale
  // snapshot (see BFK-2060 review referenced in model-comparison).
  const handleResetFilters = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of RESET_KEYS) next.delete(key);
    next.delete('taskfit_dimension');
    next.delete('taskfit_model_provider');
    next.delete('taskfit_hidden_families');
    next.delete('taskfit_show_unknown');
    next.delete('taskfit_metric');
    next.delete('taskfit_cell_row');
    next.delete('taskfit_cell_family');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const dateSection = (
    <Stack gap={16}>
      <Group gap="xs">
        <Icon name="analytics" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.task_fit.metric_label')}
        </Text>
      </Group>
      <Select
        aria-label={t('agent_miner.task_fit.metric_label')}
        data={metricOptions}
        value={effectiveMetric}
        onChange={(next) => {
          if (next) setMetric(next as TaskFitMetric);
        }}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      <Divider />
      <Group gap="xs">
        <Icon name="schedule" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.started')}
        </Text>
      </Group>
      <Select
        data={TASK_FIT_DATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
        // A stale "All time" from the shared Sessions param shows as the default.
        value={value.dateRange === 'all' ? DEFAULT_DATE_PRESET : value.dateRange}
        onChange={(next) => setDateRange((next ?? DEFAULT_DATE_PRESET) as DateRangePreset)}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      {value.dateRange === 'custom' && (
        <DatePickerInput
          type="range"
          placeholder={t('agent_miner.task_fit.pick_dates')}
          value={[toDateOrNull(value.customStart), toDateOrNull(value.customEnd)]}
          onChange={(range) => {
            const [start, end] = range as DatesRangeValue;
            setCustomRange(toIsoDateOrNull(start), toIsoDateOrNull(end));
          }}
          valueFormatter={({ date }) => {
            if (!Array.isArray(date)) return '';
            const [start, end] = date;
            if (!start || !end) return '';
            return `${dayjs(start).format('MM/DD/YYYY')} – ${dayjs(end).format('MM/DD/YYYY')}`;
          }}
          maxDate={new Date()}
          size="sm"
          radius="xl"
          clearable
        />
      )}
      <Divider />
      <Switch
        label={t('agent_miner.task_fit.show_unknown')}
        checked={showUnknown}
        onChange={(event) => setShowUnknown(event.currentTarget.checked)}
        size="sm"
      />
    </Stack>
  );

  return (
    <FilterPanel
      headerSection={dateSection}
      filterGroups={filterGroups}
      onResetFilters={handleResetFilters}
      onClose={onClose}
    />
  );
};

/**
 * Main-section content for the Task Fit tab: a dimension toggle (raw type vs
 * Flora label) above the rework-rate matrix. The heatmap metric (rework, cycle
 * time, cost) is chosen from the filter panel, not here. Owns the loading,
 * error, and empty states.
 *
 * @returns {JSX.Element} The matrix filling the main section
 */
export const TaskFitMain = () => {
  const { t } = useTranslation();
  const params = useTaskFitParams();
  const { dimension, setDimension } = useTaskFitDimension();
  // The same cost-gated metric TaskFitFilters' Select offers — see
  // useTaskFitEffectiveMetric for why this isn't derived independently here.
  const { effectiveMetric } = useTaskFitEffectiveMetric();
  const { setCell } = useTaskFitSelectedCell();
  const { providers } = useTaskFitProviderFilter();
  const { hiddenFamilies } = useTaskFitHiddenFamilies();
  const { showUnknown } = useTaskFitShowUnknown();
  const { data, query } = useTaskFit(params);

  // The model rows the matrix shows: the payload's columns narrowed by the
  // provider ∩ not-hidden ∩ (Unknown shown or not) filters. Computed before the
  // early returns below — hooks can't follow a conditional return.
  const visibleColumns = useMemo(
    () => filterTaskFitColumns(data?.columns ?? [], { providers, hiddenFamilies, showUnknown }),
    [data?.columns, providers, hiddenFamilies, showUnknown],
  );

  if (query.isError) {
    return (
      <CenteredState>
        <Icon name="error" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {t('agent_miner.task_fit.error')}
        </Text>
      </CenteredState>
    );
  }

  if (query.isPending || !data) {
    return (
      <CenteredState>
        <Text size="regular" color={inkLight}>
          {t('agent_miner.task_fit.loading')}
        </Text>
      </CenteredState>
    );
  }

  const dimensionToggle = (
    <Group gap="xs" data-testid="task-fit-dimension-toggle">
      <StyledButton
        label={t('agent_miner.task_fit.dimension_raw_type')}
        iconName="category"
        selected={dimension === 'raw_type'}
        onClick={() => setDimension('raw_type')}
      />
      <StyledButton
        label={t('agent_miner.task_fit.dimension_flora_label')}
        iconName="smart_toy"
        selected={dimension === 'flora_label'}
        onClick={() => setDimension('flora_label')}
      />
    </Group>
  );

  const hasAnyData = data.rows.length > 0 && data.columns.length > 0;

  if (!hasAnyData || visibleColumns.length === 0) {
    // Distinguish a window with no data at all from one where the active
    // filters hid every model — the fix differs (widen the window vs. loosen
    // the filters).
    const emptyMessage = hasAnyData ? t('agent_miner.task_fit.empty_filtered') : t('agent_miner.task_fit.empty');
    return (
      <Flex direction="column" gap={16} h="100%" data-testid="task-fit-main">
        <Header>
          <TitleBlock>
            <H3>{t('agent_miner.task_fit.matrix_title')}</H3>
            <Text size="small" color={inkLight}>
              {t('agent_miner.task_fit.matrix_caption')}
            </Text>
          </TitleBlock>
          {dimensionToggle}
        </Header>
        <CenteredState>
          <Icon name="insights" color={inkLight} size={48} />
          <Text size="regular" color={inkLight}>
            {emptyMessage}
          </Text>
        </CenteredState>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={16} h="100%" data-testid="task-fit-main">
      <Header>
        <TitleBlock>
          <H3>{t('agent_miner.task_fit.matrix_title')}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.task_fit.matrix_caption')}
          </Text>
        </TitleBlock>
        {dimensionToggle}
      </Header>
      <MatrixArea>
        <TaskFitMatrix
          rows={data.rows}
          columns={visibleColumns}
          dimension={dimension}
          minCellTasks={data.meta.min_cell_tasks}
          metric={effectiveMetric}
          onSelectCell={(rowKey, modelFamily) => setCell({ rowKey, modelFamily })}
        />
      </MatrixArea>
    </Flex>
  );
};

/**
 * Centered icon-and-message block used for the loading, error, and empty states.
 *
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {JSX.Element} The centered state block
 */
const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <Flex direction="column" align="center" justify="center" gap={12} mih={280} h="100%">
    {children}
  </Flex>
);

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 auto;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MatrixArea = styled.div`
  flex: 1;
  min-height: 0;
`;
